import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { routes } from 'flow/types/routes';
import Page from 'flow/components/Setting/Page';
import useAppContext from 'flow/hooks/useAppContext';
import {
    getBlindTransferSetting,
    saveBlindTransferSettings,
} from 'flow/utils/browser-utils';
import { BLIND_TRANSFER_SETTINGS } from 'flow/utils/call-utils';
import Toggle from 'flow/components/ui-components/Toggle';
import SettingRow from 'flow/components/Setting/SettingRow';
import ReferralNumberCombobox, {
    ReferralNumberOption,
} from 'flow/components/Setting/components/ReferralNumberCombobox';
import SettingSection from 'flow/components/Setting/SettingSection';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { generatePath } from 'flow/utils/router-utils';

const CallReturnOnBlindTransfer = () => {
    const { i18next } = useAppContext();
    const history = useHistory();
    const delays = [5, 10, 15, 20, 25, 30, 45, 60, 90, 120];

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const [numberForReturn, setNumberForReturn] = useState(
        getBlindTransferSetting(BLIND_TRANSFER_SETTINGS.RETURN_DESTINATION),
    );
    const [timeToReturn, setTimeToReturn] = useState(
        getBlindTransferSetting(BLIND_TRANSFER_SETTINGS.RETURN_DELAY) || 15,
    );

    const [blindTransferReturns, setBlindTransferReturns] = useState(
        getBlindTransferSetting(BLIND_TRANSFER_SETTINGS.ACTIVE),
    );

    const [referralNumber, setReferralNumber] = useState<ReferralNumberOption>({
        label: numberForReturn || i18next.t('settings.number'),
        dto: {},
    });

    const updateBlindTransferReturn = () => {
        saveBlindTransferSettings(
            BLIND_TRANSFER_SETTINGS.ACTIVE,
            !blindTransferReturns,
        );
        setBlindTransferReturns(!blindTransferReturns);
    };

    const handleReferralNumberChange = (number: ReferralNumberOption) => {
        if (!number) {
            return;
        }
        setReferralNumber(number);
        saveBlindTransferSettings(
            BLIND_TRANSFER_SETTINGS.RETURN_DESTINATION,
            number.dto.get('number'),
        );
        setNumberForReturn(number.dto.get('number'));
    };

    const handleSaveTime = (time: string) => {
        saveBlindTransferSettings(BLIND_TRANSFER_SETTINGS.RETURN_DELAY, time);
        setTimeToReturn(time);
    };

    const formatDelayTimeText = (seconds: number) => {
        if (seconds < 60) {
            return i18next.t('settings.incoming_calls.delay.seconds', {
                nbrSeconds: seconds,
            });
        }

        if (seconds === 60) {
            return i18next.t('settings.incoming_calls.delay.minute');
        }

        return i18next.t('settings.incoming_calls.delay.minutes', {
            nbrMinutes: moment.duration(seconds * 1000).asMinutes(),
        });
    };

    return (
        <Page
            header="settings.callrouting.call_return.blind_transfer"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone.advanced'], {
                        extensionKey,
                    }),
                )
            }
        >
            <div className="flex flex-col gap-1">
                <SettingRow
                    key="settings.callrouting.call_return.blind_transfer"
                    disabled={!numberForReturn}
                    cta={
                        <Toggle
                            value={blindTransferReturns}
                            onChange={updateBlindTransferReturn}
                            disabled={!numberForReturn}
                            text={i18next.t(
                                'settings.callrouting.call_return.blind_transfer',
                            )}
                        />
                    }
                >
                    {i18next.t(
                        'settings.callrouting.call_return.blind_transfer',
                    )}
                </SettingRow>

                <SettingSection className="text-sm/4">
                    {i18next.t(
                        'settings.callrouting.call_return.blind_transfer.number',
                    )}
                </SettingSection>

                <ReferralNumberCombobox
                    ariaLabel={i18next.t(
                        'settings.callrouting.call_return.blind_transfer.number',
                    )}
                    referralNumber={referralNumber}
                    onChange={handleReferralNumberChange}
                />

                <SettingSection className="text-sm/4">
                    {i18next.t(
                        'settings.callrouting.call_return.blind_transfer.wait_time',
                    )}
                </SettingSection>

                <DropdownMenuComponent
                    options={delays.map((delay) => ({
                        key: delay.toString(),
                        label: formatDelayTimeText(delay),
                    }))}
                    title={timeToReturn}
                    onItemSelected={handleSaveTime}
                />

                <div className="text-xs text-gray-500">
                    <span className="my-3 block">
                        {i18next.t(
                            'settings.callrouting.call_return.blind_transfer.enabled_info',
                        )}
                    </span>

                    <span className="block">
                        {i18next.t(
                            'settings.callrouting.call_return.blind_transfer.disabled_info',
                        )}
                    </span>
                </div>
            </div>
        </Page>
    );
};

export default CallReturnOnBlindTransfer;
